import { useQuery } from '@tanstack/react-query';

import { payrunQueryKeys } from '@xemplo/gp-constants';
import {
  ExternalPayrun,
  GetPayrunApproverDetails,
  ListPagedResult,
  ListResultV2,
  PayrunApprover,
  PayrunAssignee,
  PayrunExtended,
  PayrunListItem,
  Result,
} from '@xemplo/gp-types';
import { useHttpClient } from '@xemplo/http';
import { apiUrlBuilder } from '@xemplo/url-helper';
import { useAppSettings } from '@xemplo/xemplo-app-settings';

import {
  PagedRequestProps,
  UseGetExternalPayrunsQueryProps,
  UseGetPayrunApproversListQueryProps,
  UseGetPayrunAssigneeHistoryQueryProps,
  UseGetPayrunByIdQueryProps,
} from '../payrun-query.types';

import { PayrunAPIV2 } from './payrun-query.constants';

export const useGetPayrunListQuery = (props: PagedRequestProps) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { requestParams } = props;
  const url = apiUrlBuilder(PayrunAPIV2.payruns, applicationApiUrl, requestParams);

  return useQuery<ListPagedResult<PayrunListItem>>({
    queryKey: payrunQueryKeys.list(url),
    queryFn: ({ signal }) =>
      client
        .get<ListPagedResult<PayrunListItem>>(url, { signal })
        .then((resp) => resp.data),
    enabled: !!applicationApiUrl,
  });
};

export const useGetPayrunByIdQuery = (props: UseGetPayrunByIdQueryProps) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { requestParams, id } = props;
  const url = apiUrlBuilder(PayrunAPIV2.payrunById(id), applicationApiUrl, requestParams);

  return useQuery<Result<PayrunExtended>>({
    queryKey: payrunQueryKeys.detail(String(id ?? ''), url),
    queryFn: () => client.get<Result<PayrunExtended>>(url).then((resp) => resp.data),
    enabled: !!(id && applicationApiUrl),
  });
};

export const useGetPayrunApproversListQuery = (
  props: UseGetPayrunApproversListQueryProps,
) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { requestParams, companyId } = props;
  const url = apiUrlBuilder(
    PayrunAPIV2.payrunApprovers(companyId),
    applicationApiUrl,
    requestParams,
  );

  return useQuery<ListPagedResult<PayrunApprover>>({
    queryKey: payrunQueryKeys.approvers.list(companyId, url),
    queryFn: ({ signal }) =>
      client
        .get<ListPagedResult<PayrunApprover>>(url, { signal })
        .then((resp) => resp.data),
    enabled: !!applicationApiUrl && !!companyId,
  });
};

export const useGetPayrunApproversDetailQuery = (props: GetPayrunApproverDetails) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { companyId, userId } = props;
  const url = apiUrlBuilder(
    PayrunAPIV2.payrunApproversDetails(companyId, userId),
    applicationApiUrl,
  );

  return useQuery({
    queryKey: payrunQueryKeys.approvers.detail(companyId ?? '', userId ?? '', url),
    queryFn: ({ signal }) =>
      client
        .get<Result<PayrunApprover>>(url, { signal })
        .then((resp) => resp.data)
        .then((data) => data.result),
    enabled: !!applicationApiUrl && !!companyId && !!userId,
    select: (data) => {
      return {
        ...data,
        formDefaults: {
          payrunTypeId: data.payrunTypeId,
          payschedules: data.payschedules.map((p) => p.id),
        },
      };
    },
  });
};

export const useGetPayrunAssigneeHistoryQuery = (
  props: UseGetPayrunAssigneeHistoryQueryProps,
) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { payrunId } = props;
  const url = apiUrlBuilder(PayrunAPIV2.assigneeHistory(payrunId), applicationApiUrl);

  return useQuery({
    queryKey: payrunQueryKeys.assigneeHistory(payrunId?.toString() ?? '', url),
    queryFn: ({ signal }) =>
      client.get<ListResultV2<PayrunAssignee>>(url, { signal }).then((resp) => resp.data),
    enabled: !!applicationApiUrl && !!payrunId,
  });
};

export const useGetExternalPayrunsQuery = (props: UseGetExternalPayrunsQueryProps) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { payrunId, requestParams } = props;
  const url = apiUrlBuilder(
    PayrunAPIV2.externalPayruns(payrunId),
    applicationApiUrl,
    requestParams,
  );

  return useQuery({
    queryKey: payrunQueryKeys.externalPayruns(payrunId?.toString() ?? '', url),
    queryFn: ({ signal }) =>
      client.get<ListResultV2<ExternalPayrun>>(url, { signal }).then((resp) => resp.data),
    enabled: !!applicationApiUrl && !!payrunId,
  });
};
